import {
  buildSmoothAreaPath,
  buildSmoothLinePath,
  CHART_COMPARE_COLOR,
} from "@/analytics/core/chart-palette";
import { EXPLORE_CHART_HEIGHT, resolveExploreChartWidth } from "@/analytics/core/chart-layout";
import type { ChartSeries, TrendPoint } from "@/analytics/core/types";
import { formatMetricValue } from "@/grid/core/format-metric";
import type { MetricFormat } from "@/grid/core/types";
import { cn } from "@/lib/cn";
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

interface ExploreAreaChartProps {
  series: ChartSeries[];
  compareSeries?: TrendPoint[];
  format: MetricFormat;
  compareLabel?: string;
  className?: string;
}

interface HoverState {
  index: number;
  x: number;
  y: number;
}

const CHART_HEIGHT = EXPLORE_CHART_HEIGHT;
const PADDING = { top: 16, right: 16, bottom: 36, left: 48 };

function isAllZero(series: ChartSeries[], compareSeries: TrendPoint[]): boolean {
  const values = [
    ...series.flatMap((s) => s.points.map((point) => point.value)),
    ...compareSeries.map((point) => point.value),
  ];
  return values.length > 0 && values.every((value) => value === 0);
}

function emptyChartMessage(format: MetricFormat, seriesEmpty: boolean): string {
  if (format === "currency" && seriesEmpty) {
    return "No chart data for this period. Sales and opportunities are stored by week or month, not daily. Try a longer range or confirm data has synced.";
  }
  return "No chart data for this period";
}

function sparseChartMessage(format: MetricFormat): string {
  if (format === "currency") {
    return "No sales or opportunities recorded for this period. Values are stored by week or month, so short ranges may look flat until activity lands.";
  }
  return "No activity recorded for this period. Values are zero across the selected range.";
}

function ChartStateMessage({ message, className }: { message: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex h-[320px] items-center justify-center px-6 text-center text-sm text-[var(--color-text-muted)]",
        className,
      )}
    >
      {message}
    </div>
  );
}

export function ExploreAreaChart({
  series,
  compareSeries = [],
  format,
  compareLabel,
  className,
}: ExploreAreaChartProps) {
  const gradientId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const syncWidth = () => {
      setWidth(resolveExploreChartWidth(node.getBoundingClientRect().width));
    };

    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pointCount = series[0]?.points.length ?? compareSeries.length;
  const primarySeries = series[0];

  const geometry = useMemo(() => {
    if (pointCount === 0) return null;

    const allValues = [
      ...series.flatMap((s) => s.points.map((p) => p.value)),
      ...compareSeries.map((p) => p.value),
    ];
    const min = Math.min(...allValues, 0);
    const max = Math.max(...allValues, 1);
    const range = max - min || 1;

    const innerWidth = width - PADDING.left - PADDING.right;
    const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
    const baseY = PADDING.top + innerHeight;

    const xAt = (index: number) =>
      PADDING.left + (pointCount === 1 ? innerWidth / 2 : (index / (pointCount - 1)) * innerWidth);

    const yAt = (value: number) =>
      PADDING.top + innerHeight - ((value - min) / range) * innerHeight;

    const toCoords = (points: TrendPoint[]) =>
      points.map((point, index) => ({ x: xAt(index), y: yAt(point.value) }));

    const buildPath = (points: TrendPoint[]) => buildSmoothLinePath(toCoords(points));
    const buildArea = (points: TrendPoint[]) => buildSmoothAreaPath(toCoords(points), baseY);

    const yTicks = Array.from({ length: 4 }, (_, i) => min + (range * i) / 3);

    return { xAt, yAt, min, max, innerHeight, baseY, buildPath, buildArea, yTicks, innerWidth };
  }, [series, compareSeries, pointCount, width]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!geometry || !containerRef.current || pointCount === 0) return;

      const rect = containerRef.current.getBoundingClientRect();
      const relativeX = event.clientX - rect.left - PADDING.left;
      const index =
        pointCount === 1
          ? 0
          : Math.max(
              0,
              Math.min(
                pointCount - 1,
                Math.round((relativeX / geometry.innerWidth) * (pointCount - 1)),
              ),
            );

      setHover({
        index,
        x: geometry.xAt(index),
        y: event.clientY - rect.top,
      });
    },
    [geometry, pointCount],
  );

  if (!geometry || !primarySeries) {
    return (
      <ChartStateMessage
        message={emptyChartMessage(format, series.length === 0 || pointCount === 0)}
        className={className}
      />
    );
  }

  if (width === 0) {
    return (
      <div
        ref={containerRef}
        className={cn("relative select-none", className)}
        style={{ height: CHART_HEIGHT }}
        aria-hidden
      />
    );
  }

  if (isAllZero(series, compareSeries)) {
    return <ChartStateMessage message={sparseChartMessage(format)} className={className} />;
  }

  const hoverIndex = hover?.index ?? null;
  const hoverPoint = hoverIndex != null ? primarySeries.points[hoverIndex] : null;

  return (
    <div
      ref={containerRef}
      className={cn("relative select-none", className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        width={width}
        height={CHART_HEIGHT}
        viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
        className="block max-w-full"
        role="img"
        aria-label="Trend chart"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={primarySeries.color} stopOpacity="0.16" />
            <stop offset="100%" stopColor={primarySeries.color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {geometry.yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={geometry.yAt(tick)}
              y2={geometry.yAt(tick)}
              stroke="#E8ECF0"
              strokeDasharray="3 5"
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={geometry.yAt(tick) + 4}
              textAnchor="end"
              className="fill-[#94A3B8] text-[10px]"
            >
              {formatMetricValue(tick, format === "currency" ? "compact" : format)}
            </text>
          </g>
        ))}

        {series.length === 1 ? (
          <>
            <path d={geometry.buildArea(primarySeries.points)} fill={`url(#${gradientId})`} />
            <path
              d={geometry.buildPath(primarySeries.points)}
              fill="none"
              stroke={primarySeries.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : (
          series.map((s) => (
            <path
              key={s.key}
              d={geometry.buildPath(s.points)}
              fill="none"
              stroke={s.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))
        )}

        {compareSeries.length > 0 && series.length === 1 ? (
          <path
            d={geometry.buildPath(compareSeries)}
            fill="none"
            stroke={CHART_COMPARE_COLOR}
            strokeWidth={2}
            strokeDasharray="5 5"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.7}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}

        {hoverIndex != null ? (
          <>
            <line
              x1={geometry.xAt(hoverIndex)}
              x2={geometry.xAt(hoverIndex)}
              y1={PADDING.top}
              y2={PADDING.top + geometry.innerHeight}
              stroke={primarySeries.color}
              strokeDasharray="3 4"
              opacity={0.35}
            />
            {series.map((s) => {
              const point = s.points[hoverIndex];
              if (!point) return null;
              return (
                <circle
                  key={s.key}
                  cx={geometry.xAt(hoverIndex)}
                  cy={geometry.yAt(point.value)}
                  r={5}
                  fill={s.color}
                  stroke="white"
                  strokeWidth={2.5}
                />
              );
            })}
          </>
        ) : null}

        {primarySeries.points.map((point, index) => (
          <text
            key={point.date}
            x={geometry.xAt(index)}
            y={CHART_HEIGHT - 10}
            textAnchor="middle"
            className="fill-[#94A3B8] text-[10px]"
          >
            {point.label ?? point.date.slice(5)}
          </text>
        ))}
      </svg>

      {hoverPoint && hover ? (
        <div
          className="pointer-events-none absolute z-10 rounded-xl border border-[#E2E8F0] bg-white/95 px-3 py-2.5 text-xs shadow-lg backdrop-blur-sm"
          style={{
            left: Math.min(hover.x, width - 168),
            top: Math.max(8, hover.y - 80),
          }}
        >
          <p className="mb-1.5 font-medium text-[#64748B]">{hoverPoint.label ?? hoverPoint.date}</p>
          {series.map((s) => {
            const point = s.points[hover?.index ?? 0];
            if (!point) return null;
            const comparePoint = s.comparePoints?.[hover?.index ?? 0];
            const bucketDelta =
              comparePoint != null && comparePoint.value !== 0
                ? ((point.value - comparePoint.value) / comparePoint.value) * 100
                : comparePoint != null && point.value !== 0
                  ? null
                  : comparePoint != null
                    ? 0
                    : null;
            return (
              <p key={s.key} className="flex items-center justify-between gap-6 text-[#334155]">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
                <span className="flex items-center gap-2 font-semibold">
                  {formatMetricValue(point.value, format)}
                  {bucketDelta != null ? (
                    <span
                      className={cn(
                        "text-[10px] font-medium",
                        bucketDelta > 0
                          ? "text-emerald-600"
                          : bucketDelta < 0
                            ? "text-red-600"
                            : "text-[#94A3B8]",
                      )}
                    >
                      {bucketDelta > 0 ? "+" : ""}
                      {bucketDelta.toFixed(0)}%
                    </span>
                  ) : null}
                </span>
              </p>
            );
          })}
          {compareSeries[hover.index] != null && series.length === 1 ? (
            <p className="mt-1.5 flex items-center justify-between gap-6 border-t border-[#EEF2F6] pt-1.5 text-[#64748B]">
              <span>{compareLabel ?? "Compare"}</span>
              <span className="font-medium">
                {formatMetricValue(compareSeries[hover.index]!.value, format)}
              </span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
